import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Send, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import type { ConversationType } from '../../services/messagingService';

interface StartConversationButtonProps {
  participantId?: string;
  participantEmail?: string;
  participantName: string;
  participantRole: 'CLIENT' | 'PROFESSIONAL' | 'ADMIN';
  contactRequestId?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
  disabled?: boolean;
}

export function StartConversationButton({
  participantId,
  participantEmail,
  participantName,
  participantRole,
  contactRequestId,
  variant = 'outline',
  size = 'default',
  className,
  disabled = false,
}: StartConversationButtonProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createConversation } = useMessaging();
  const [isOpen, setIsOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getConversationType = (): ConversationType => {
    const userRole = user?.role?.toUpperCase();
    
    if (userRole === 'ADMIN') {
      if (participantRole === 'CLIENT') return 'CLIENT_ADMIN';
      if (participantRole === 'PROFESSIONAL') return 'ADMIN_PRO';
    }
    
    if (userRole === 'CLIENT') {
      if (participantRole === 'ADMIN') return 'CLIENT_ADMIN';
      if (participantRole === 'PROFESSIONAL') return 'CLIENT_PRO';
    }
    
    if (userRole === 'PROFESSIONAL') {
      if (participantRole === 'ADMIN') return 'ADMIN_PRO';
      if (participantRole === 'CLIENT') return 'CLIENT_PRO';
    }
    
    return 'CLIENT_ADMIN'; // fallback
  };

  const getMessagesRoute = () => {
    const userRole = user?.role?.toLowerCase();
    if (userRole === 'admin') return '/admin/messages';
    if (userRole === 'professional') return '/professional/messages';
    return '/client/messages';
  };

  const handleStartConversation = async () => {
    if (!user) return;

    if (!participantId && !contactRequestId) {
      setError('Impossible de démarrer la conversation : participant manquant.');
      return;
    }
    
    setIsLoading(true);
    try {
      setError(null);
      const conversation = await createConversation(
        getConversationType(),
        participantId,
        contactRequestId,
        initialMessage.trim() || undefined
      );
      
      setIsOpen(false);
      setInitialMessage('');
      
      // Navigate to messages page with conversation selected
      navigate(getMessagesRoute());
    } catch (err: any) {
      console.error('Error starting conversation:', err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Impossible de démarrer la conversation. Veuillez réessayer.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = disabled;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          className={className}
          disabled={isDisabled}
          title={error || undefined}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Envoyer un message
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Démarrer une conversation</DialogTitle>
          <DialogDescription>
            Envoyez un message à {participantName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="initial-message">Message (optionnel)</Label>
            <Textarea
              id="initial-message"
              placeholder="Écrivez votre premier message..."
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              rows={4}
            />
          </div>
        </div>
        {error && (
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleStartConversation}
            disabled={isLoading || (!participantId && !contactRequestId)}
            className="bg-[#FF9F68] hover:bg-[#E67E4F]"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {initialMessage.trim() ? 'Envoyer' : 'Ouvrir la conversation'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default StartConversationButton;

